import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { 
  Car, Bell, LogOut, MapPin, Clock, Calendar, 
  CheckCircle, AlertCircle, Loader2, User, Plus, Zap,
  CreditCard, Star, DollarSign, ExternalLink
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";
import { useAuth, API, BACKEND_URL } from "@/App";

const CustomerDashboard = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, logout } = useAuth();
  const [venues, setVenues] = useState([]);
  const [myRequests, setMyRequests] = useState([]);
  const [myBookings, setMyBookings] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showNotifications, setShowNotifications] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);
  const wsRef = useRef(null);

  // Valet request form
  const [valetDialogOpen, setValetDialogOpen] = useState(false);
  const [selectedVenue, setSelectedVenue] = useState("");
  const [carDetails, setCarDetails] = useState("");
  const [parkingToken, setParkingToken] = useState("");

  // Driver booking form
  const [driverDialogOpen, setDriverDialogOpen] = useState(false);
  const [driverVenue, setDriverVenue] = useState("");
  const [pickupTime, setPickupTime] = useState("");
  const [pickupLocation, setPickupLocation] = useState("");
  const [destination, setDestination] = useState("");
  const [driverNotes, setDriverNotes] = useState("");

  // Payment state
  const [packages, setPackages] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState("");
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [paymentBookingId, setPaymentBookingId] = useState(null);

  // Rating state
  const [ratingDialogOpen, setRatingDialogOpen] = useState(false);
  const [ratingServiceType, setRatingServiceType] = useState("");
  const [ratingServiceId, setRatingServiceId] = useState("");
  const [ratingStars, setRatingStars] = useState(0);
  const [ratingHover, setRatingHover] = useState(0);
  const [ratingReview, setRatingReview] = useState("");
  const [ratingLoading, setRatingLoading] = useState(false);
  const [myRatings, setMyRatings] = useState([]);

  // Handle QR code scanned venue
  useEffect(() => {
    const venueFromQR = searchParams.get('venue');
    if (venueFromQR && venues.length > 0 && !loading) {
      const venueExists = venues.find(v => v.venue_id === venueFromQR);
      if (venueExists && !valetDialogOpen) {
        setSelectedVenue(venueFromQR);
        setTimeout(() => {
          setValetDialogOpen(true);
          toast.success(`Welcome to ${venueExists.name}! Request your car when ready.`);
        }, 100);
      }
    }
  }, [searchParams, venues, loading, valetDialogOpen]);

  // Handle return from Stripe payment
  useEffect(() => {
    const paymentStatus = searchParams.get('payment');
    const sessionId = searchParams.get('session_id');
    
    if (paymentStatus === 'success' && sessionId) {
      // Poll payment status
      const pollPayment = async (attempts = 0) => {
        if (attempts >= 5) {
          toast.error("Payment verification timed out. Check your bookings for status.");
          return;
        }
        try {
          const response = await axios.get(`${API}/payments/status/${sessionId}`, { withCredentials: true });
          if (response.data.payment_status === 'paid') {
            toast.success("Payment successful! Your driver booking is confirmed.");
            fetchData();
            // Clean URL
            window.history.replaceState({}, '', window.location.pathname);
          } else {
            setTimeout(() => pollPayment(attempts + 1), 2000);
          }
        } catch (error) {
          console.error("Payment status check failed:", error);
          setTimeout(() => pollPayment(attempts + 1), 2000);
        }
      };
      pollPayment();
    } else if (paymentStatus === 'cancelled') {
      toast.warning("Payment was cancelled.");
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [searchParams]);

  // Fetch driver packages
  useEffect(() => {
    const fetchPackages = async () => {
      try {
        const response = await axios.get(`${API}/driver/packages`);
        setPackages(response.data);
        if (response.data.length > 0) setSelectedPackage(response.data[0].package_id);
      } catch (error) {
        console.error("Error fetching packages:", error);
      }
    };
    fetchPackages();
  }, []);

  // WebSocket connection for real-time updates
  useEffect(() => {
    if (!user?.user_id) return;

    const wsProtocol = BACKEND_URL.startsWith('https') ? 'wss' : 'ws';
    const wsHost = BACKEND_URL.replace(/^https?:\/\//, '');
    const wsUrl = `${wsProtocol}://${wsHost}/api/ws/customer/${user.user_id}`;

    const connectWebSocket = () => {
      try {
        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;

        ws.onopen = () => {
          setWsConnected(true);
          console.log('WebSocket connected');
        };

        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            if (data.type === 'status_update') {
              // Update request status in real-time
              setMyRequests(prev => prev.map(req => 
                req.request_id === data.request_id 
                  ? { ...req, status: data.status }
                  : req
              ));
              toast.success(data.message, {
                icon: data.status === 'ready' ? '🚗' : '✓'
              });
              setUnreadCount(prev => prev + 1);
            }
          } catch (e) {
            console.error('WebSocket message error:', e);
          }
        };

        ws.onclose = () => {
          setWsConnected(false);
          console.log('WebSocket disconnected, reconnecting...');
          setTimeout(connectWebSocket, 3000);
        };

        ws.onerror = (error) => {
          console.error('WebSocket error:', error);
        };
      } catch (error) {
        console.error('WebSocket connection error:', error);
      }
    };

    connectWebSocket();

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [user?.user_id]);

  const fetchData = useCallback(async () => {
    try {
      const [venuesRes, requestsRes, bookingsRes, notifRes, unreadRes, ratingsRes] = await Promise.all([
        axios.get(`${API}/venues`, { withCredentials: true }),
        axios.get(`${API}/valet/my-requests`, { withCredentials: true }),
        axios.get(`${API}/driver/my-bookings`, { withCredentials: true }),
        axios.get(`${API}/notifications`, { withCredentials: true }),
        axios.get(`${API}/notifications/unread-count`, { withCredentials: true }),
        axios.get(`${API}/ratings/my-ratings`, { withCredentials: true })
      ]);
      setVenues(venuesRes.data);
      setMyRequests(requestsRes.data);
      setMyBookings(bookingsRes.data);
      setNotifications(notifRes.data);
      setUnreadCount(unreadRes.data.count);
      setMyRatings(ratingsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    // Poll for updates every 10 seconds
    const interval = setInterval(fetchData, 10000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleValetRequest = async () => {
    if (!selectedVenue || !carDetails) {
      toast.error("Please select a venue and enter car details");
      return;
    }

    try {
      await axios.post(`${API}/valet/request`, {
        venue_id: selectedVenue,
        car_details: carDetails,
        parking_token: parkingToken || null
      }, { withCredentials: true });
      
      toast.success("Valet request sent! Your car will be ready soon.");
      setValetDialogOpen(false);
      setSelectedVenue("");
      setCarDetails("");
      setParkingToken("");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send request");
    }
  };

  const handleDriverBooking = async () => {
    if (!driverVenue || !pickupTime || !pickupLocation || !destination) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      const response = await axios.post(`${API}/driver/book`, {
        venue_id: driverVenue,
        pickup_time: pickupTime,
        pickup_location: pickupLocation,
        destination: destination,
        notes: driverNotes || null
      }, { withCredentials: true });
      
      toast.success("Booking created! Select a package to pay.");
      setDriverDialogOpen(false);
      setDriverVenue("");
      setPickupTime("");
      setPickupLocation("");
      setDestination("");
      setDriverNotes("");
      fetchData();
      
      // Open payment dialog for this booking
      setPaymentBookingId(response.data.booking_id);
      setPaymentDialogOpen(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to book driver");
    }
  };

  const handlePayment = async (bookingId) => {
    if (!selectedPackage) {
      toast.error("Please select a package");
      return;
    }
    
    setPaymentLoading(true);
    try {
      const response = await axios.post(`${API}/payments/create-checkout`, {
        booking_id: bookingId,
        package_id: selectedPackage,
        origin_url: window.location.origin
      }, { withCredentials: true });
      
      setPaymentDialogOpen(false);
      setPaymentBookingId(null);
      setSelectedPackage("");
      toast.success("Payment successful! Your driver booking is confirmed.");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to initiate payment");
    } finally {
      setPaymentLoading(false);
    }
  };

  const openPaymentForBooking = (bookingId) => {
    setPaymentBookingId(bookingId);
    setPaymentDialogOpen(true);
  };

  const getPaymentBadge = (status) => {
    if (status === "paid") return <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 border text-xs"><CheckCircle className="w-3 h-3 mr-1" />Paid</Badge>;
    if (status === "initiated") return <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 border text-xs"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
    return <Badge className="bg-white/10 text-[#A1A1AA] border-white/10 border text-xs"><DollarSign className="w-3 h-3 mr-1" />Unpaid</Badge>;
  };

  // Rating functions
  const openRatingDialog = (serviceType, serviceId) => {
    setRatingServiceType(serviceType);
    setRatingServiceId(serviceId);
    setRatingStars(0);
    setRatingHover(0);
    setRatingReview("");
    setRatingDialogOpen(true);
  };

  const handleSubmitRating = async () => {
    if (ratingStars === 0) {
      toast.error("Please select a star rating");
      return;
    }
    setRatingLoading(true);
    try {
      await axios.post(`${API}/ratings/submit`, {
        service_type: ratingServiceType,
        service_id: ratingServiceId,
        rating: ratingStars,
        review: ratingReview || null
      }, { withCredentials: true });
      toast.success("Thank you for your rating!");
      setRatingDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit rating");
    } finally {
      setRatingLoading(false);
    }
  };

  const hasRated = (serviceType, serviceId) => {
    return myRatings.some(r => r.service_type === serviceType && r.service_id === serviceId);
  };

  const getRating = (serviceType, serviceId) => {
    return myRatings.find(r => r.service_type === serviceType && r.service_id === serviceId);
  };

  const markAllRead = async () => {
    try {
      await axios.put(`${API}/notifications/read-all`, {}, { withCredentials: true });
      setUnreadCount(0);
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    } catch (error) {
      console.error("Error marking notifications as read:", error);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      requested: { class: "status-requested", icon: <Clock className="w-3 h-3" />, label: "Requested" },
      acknowledged: { class: "status-requested", icon: <CheckCircle className="w-3 h-3" />, label: "Acknowledged" },
      fetching: { class: "status-fetching", icon: <Loader2 className="w-3 h-3 animate-spin" />, label: "Fetching Car" },
      ready: { class: "status-ready", icon: <CheckCircle className="w-3 h-3" />, label: "Ready!" },
      completed: { class: "status-completed", icon: <CheckCircle className="w-3 h-3" />, label: "Completed" },
      pending: { class: "status-requested", icon: <Clock className="w-3 h-3" />, label: "Pending" },
      confirmed: { class: "status-fetching", icon: <CheckCircle className="w-3 h-3" />, label: "Confirmed" },
      in_progress: { class: "status-fetching", icon: <Loader2 className="w-3 h-3 animate-spin" />, label: "In Progress" },
      cancelled: { class: "status-completed", icon: <AlertCircle className="w-3 h-3" />, label: "Cancelled" }
    };
    const config = statusConfig[status] || statusConfig.requested;
    return (
      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${config.class}`}>
        {config.icon}
        {config.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#090A0F] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#090A0F]" data-testid="customer-dashboard">
      {/* Header */}
      <header className="crystal-header sticky top-0 z-50">
        <div className="container mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-3">
            <img src="/logo.svg" alt="VeloPark" className="w-8 h-8 sm:w-10 sm:h-10 rounded-full" />
            <span className="font-display text-lg sm:text-xl font-semibold text-white">VeloPark</span>
            {wsConnected && (
              <span className="flex items-center gap-1 text-xs text-emerald-400 bg-emerald-500/10 px-2 py-1 rounded-full">
                <Zap className="w-3 h-3" />
                <span className="hidden sm:inline">Live</span>
              </span>
            )}
          </div>

          <div className="flex items-center gap-2 sm:gap-4">
            {/* Notifications */}
            <div className="relative">
              <Button
                variant="ghost"
                size="icon"
                className="text-white hover:text-[#D4AF37] hover:bg-white/5 relative"
                onClick={() => setShowNotifications(!showNotifications)}
                data-testid="notifications-btn"
              >
                <Bell className="w-5 h-5" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-xs flex items-center justify-center">
                    {unreadCount}
                  </span>
                )}
              </Button>

              {showNotifications && (
                <div className="absolute right-0 top-12 w-80 glass-card rounded-xl overflow-hidden z-50">
                  <div className="p-4 border-b border-white/10 flex items-center justify-between">
                    <span className="font-medium text-white">Notifications</span>
                    {unreadCount > 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-[#D4AF37] hover:text-[#F0C950] text-xs"
                        onClick={markAllRead}
                        data-testid="mark-all-read-btn"
                      >
                        Mark all read
                      </Button>
                    )}
                  </div>
                  <ScrollArea className="h-64">
                    {notifications.length === 0 ? (
                      <div className="p-6 text-center text-[#A1A1AA]">
                        No notifications yet
                      </div>
                    ) : (
                      notifications.map((notif) => (
                        <div
                          key={notif.notification_id}
                          className={`p-4 border-b border-white/5 ${!notif.is_read ? 'bg-[#D4AF37]/5' : ''}`}
                        >
                          <p className="text-sm font-medium text-white">{notif.title}</p>
                          <p className="text-xs text-[#A1A1AA] mt-1">{notif.message}</p>
                        </div>
                      ))
                    )}
                  </ScrollArea>
                </div>
              )}
            </div>

            {/* User Menu */}
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
                <User className="w-4 h-4 text-[#D4AF37]" />
              </div>
              <span className="text-white text-sm hidden sm:block">{user?.name}</span>
            </div>

            <Button
              variant="ghost"
              size="icon"
              className="text-[#A1A1AA] hover:text-white hover:bg-white/5"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 sm:px-6 py-4 sm:py-8">
        {/* Welcome Section */}
        <div className="mb-6 sm:mb-8">
          <h1 className="font-display text-2xl sm:text-3xl font-semibold text-white mb-2">
            Welcome back, {user?.name?.split(' ')[0]}
          </h1>
          <p className="text-[#A1A1AA]">Request your car or book a driver for your next event</p>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8">
          {/* Request Valet Card */}
          <Dialog open={valetDialogOpen} onOpenChange={setValetDialogOpen}>
            <DialogTrigger asChild>
              <div 
                className="glass-card rounded-2xl p-6 sm:p-8 cursor-pointer hover-lift"
                data-testid="request-valet-card"
              >
                <div className="flex items-start justify-between mb-6">
                  <div className="w-14 h-14 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center">
                    <Car className="w-7 h-7 text-[#D4AF37]" />
                  </div>
                  <Plus className="w-6 h-6 text-[#A1A1AA]" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">Request Your Car</h3>
                <p className="text-[#A1A1AA]">Get your car ready before you exit</p>
              </div>
            </DialogTrigger>
            <DialogContent className="bg-[#0D0E14] border-white/10 text-white">
              <DialogHeader>
                <DialogTitle className="font-display text-2xl">Request Valet</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label className="text-white">Select Venue</Label>
                  <Select value={selectedVenue} onValueChange={setSelectedVenue}>
                    <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="venue-select">
                      <SelectValue placeholder="Choose venue" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1A1B21] border-white/10">
                      {venues.map((venue) => (
                        <SelectItem key={venue.venue_id} value={venue.venue_id} className="text-white hover:bg-white/10">
                          {venue.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-white">Car Details</Label>
                  <Input
                    placeholder="e.g., Black BMW X5, License: ABC 123"
                    value={carDetails}
                    onChange={(e) => setCarDetails(e.target.value)}
                    className="bg-white/5 border-white/10 text-white"
                    data-testid="car-details-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-white">Parking Token (Optional)</Label>
                  <Input
                    placeholder="Enter if you have one"
                    value={parkingToken}
                    onChange={(e) => setParkingToken(e.target.value)}
                    className="bg-white/5 border-white/10 text-white"
                    data-testid="parking-token-input"
                  />
                </div>
                <Button 
                  className="w-full btn-primary py-6 rounded-xl"
                  onClick={handleValetRequest}
                  data-testid="submit-valet-request-btn"
                >
                  Request My Car
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Book Driver Card */}
          <Dialog open={driverDialogOpen} onOpenChange={setDriverDialogOpen}>
            <DialogTrigger asChild>
              <div 
                className="glass-card rounded-2xl p-6 sm:p-8 cursor-pointer hover-lift"
                data-testid="book-driver-card"
              >
                <div className="flex items-start justify-between mb-6">
                  <div className="w-14 h-14 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center">
                    <User className="w-7 h-7 text-[#D4AF37]" />
                  </div>
                  <Plus className="w-6 h-6 text-[#A1A1AA]" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">Book a Driver</h3>
                <p className="text-[#A1A1AA]">Professional drivers for any occasion</p>
              </div>
            </DialogTrigger>
            <DialogContent className="bg-[#0D0E14] border-white/10 text-white max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-display text-2xl">Book a Driver</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label className="text-white">Select Venue/Event</Label>
                  <Select value={driverVenue} onValueChange={setDriverVenue}>
                    <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="driver-venue-select">
                      <SelectValue placeholder="Choose venue" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1A1B21] border-white/10">
                      {venues.map((venue) => (
                        <SelectItem key={venue.venue_id} value={venue.venue_id} className="text-white hover:bg-white/10">
                          {venue.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-white">Pickup Date & Time</Label>
                  <Input
                    type="datetime-local"
                    value={pickupTime}
                    onChange={(e) => setPickupTime(e.target.value)}
                    className="bg-white/5 border-white/10 text-white"
                    data-testid="pickup-time-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-white">Pickup Location</Label>
                  <Input
                    placeholder="Enter pickup address"
                    value={pickupLocation}
                    onChange={(e) => setPickupLocation(e.target.value)}
                    className="bg-white/5 border-white/10 text-white"
                    data-testid="pickup-location-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-white">Destination</Label>
                  <Input
                    placeholder="Enter destination"
                    value={destination}
                    onChange={(e) => setDestination(e.target.value)}
                    className="bg-white/5 border-white/10 text-white"
                    data-testid="destination-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-white">Additional Notes (Optional)</Label>
                  <Input
                    placeholder="Any special requirements"
                    value={driverNotes}
                    onChange={(e) => setDriverNotes(e.target.value)}
                    className="bg-white/5 border-white/10 text-white"
                    data-testid="driver-notes-input"
                  />
                </div>
                <Button 
                  className="w-full btn-primary py-6 rounded-xl"
                  onClick={handleDriverBooking}
                  data-testid="submit-driver-booking-btn"
                >
                  Book Driver
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Activity Tabs */}
        <Tabs defaultValue="valet" className="w-full">
          <TabsList className="bg-white/5 border border-white/10 p-1 rounded-xl">
            <TabsTrigger 
              value="valet" 
              className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#090A0F] text-white rounded-lg px-6"
              data-testid="valet-tab"
            >
              Valet Requests
            </TabsTrigger>
            <TabsTrigger 
              value="driver" 
              className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#090A0F] text-white rounded-lg px-6"
              data-testid="driver-tab"
            >
              Driver Bookings
            </TabsTrigger>
          </TabsList>

          <TabsContent value="valet" className="mt-6">
            {myRequests.length === 0 ? (
              <div className="glass-card rounded-2xl p-12 text-center">
                <img 
                  src="https://static.prod-images.emergentagent.com/jobs/7c75bd88-236d-4e1a-82bf-77a03477f895/images/c7dd0448f3f4823a5defba59a972742ad9ef3103af6077dcd88c211598dd9d55.png"
                  alt="No requests"
                  className="w-32 h-32 mx-auto mb-6 opacity-50"
                />
                <h3 className="text-xl font-semibold text-white mb-2">No valet requests yet</h3>
                <p className="text-[#A1A1AA]">Request your car and it will appear here</p>
              </div>
            ) : (
              <div className="space-y-4">
                {myRequests.map((request) => (
                  <div 
                    key={request.request_id}
                    className="glass-card rounded-xl p-4 sm:p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-3"
                    data-testid={`valet-request-${request.request_id}`}
                  >
                    <div className="flex items-center gap-3 sm:gap-4">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center shrink-0">
                        <Car className="w-5 h-5 sm:w-6 sm:h-6 text-[#D4AF37]" />
                      </div>
                      <div className="min-w-0">
                        <h4 className="font-medium text-white text-sm sm:text-base truncate">{request.car_details}</h4>
                        <p className="text-xs sm:text-sm text-[#A1A1AA] flex items-center gap-2 mt-1">
                          <MapPin className="w-3 h-3 shrink-0" />
                          <span className="truncate">{request.venue_name}</span>
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 sm:gap-4 self-start sm:self-center">
                      {getStatusBadge(request.status)}
                      <span className="text-xs text-[#A1A1AA] hidden sm:block">
                        {new Date(request.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    {/* Rating section */}
                    {request.status === "completed" && (
                      <div className="w-full mt-2 pt-2 border-t border-white/5">
                        {hasRated("valet", request.request_id) ? (
                          <div className="flex items-center gap-2">
                            <div className="flex">
                              {[1,2,3,4,5].map(s => (
                                <Star key={s} className={`w-3.5 h-3.5 ${s <= getRating("valet", request.request_id)?.rating ? 'text-[#D4AF37] fill-[#D4AF37]' : 'text-white/20'}`} />
                              ))}
                            </div>
                            <span className="text-xs text-[#A1A1AA]">Rated</span>
                          </div>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            className="border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37]/10 text-xs"
                            onClick={() => openRatingDialog("valet", request.request_id)}
                            data-testid={`rate-valet-${request.request_id}`}
                          >
                            <Star className="w-3 h-3 mr-1" />
                            Rate Service
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="driver" className="mt-6">
            {myBookings.length === 0 ? (
              <div className="glass-card rounded-2xl p-12 text-center">
                <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-[#D4AF37]/10 flex items-center justify-center">
                  <Calendar className="w-10 h-10 text-[#D4AF37]" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">No driver bookings yet</h3>
                <p className="text-[#A1A1AA]">Book a driver for your next event</p>
              </div>
            ) : (
              <div className="space-y-4">
                {myBookings.map((booking) => (
                  <div 
                    key={booking.booking_id}
                    className="glass-card rounded-xl p-4 sm:p-6"
                    data-testid={`driver-booking-${booking.booking_id}`}
                  >
                    <div className="flex flex-col sm:flex-row sm:items-start justify-between mb-4 gap-3">
                      <div className="flex items-center gap-3 sm:gap-4">
                        <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center shrink-0">
                          <User className="w-5 h-5 sm:w-6 sm:h-6 text-[#D4AF37]" />
                        </div>
                        <div className="min-w-0">
                          <h4 className="font-medium text-white text-sm sm:text-base truncate">{booking.venue_name}</h4>
                          <p className="text-xs sm:text-sm text-[#A1A1AA] flex items-center gap-2 mt-1">
                            <Clock className="w-3 h-3" />
                            {new Date(booking.pickup_time).toLocaleString()}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 self-start">
                        {getPaymentBadge(booking.payment_status)}
                        {getStatusBadge(booking.status)}
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm mb-3">
                      <div>
                        <span className="text-[#A1A1AA]">From:</span>
                        <p className="text-white">{booking.pickup_location}</p>
                      </div>
                      <div>
                        <span className="text-[#A1A1AA]">To:</span>
                        <p className="text-white">{booking.destination}</p>
                      </div>
                      {booking.package_name && (
                        <div>
                          <span className="text-[#A1A1AA]">Package:</span>
                          <p className="text-white">{booking.package_name} — ₹{booking.package_amount}</p>
                        </div>
                      )}
                      {booking.driver_name && (
                        <div>
                          <span className="text-[#A1A1AA]">Driver:</span>
                          <p className="text-white">{booking.driver_name}</p>
                        </div>
                      )}
                    </div>
                    {/* Pay button for unpaid bookings */}
                    {booking.payment_status !== "paid" && booking.status !== "cancelled" && (
                      <Button
                        className="btn-primary w-full sm:w-auto mt-2"
                        size="sm"
                        onClick={() => openPaymentForBooking(booking.booking_id)}
                        data-testid={`pay-booking-${booking.booking_id}`}
                      >
                        <CreditCard className="w-4 h-4 mr-2" />
                        {booking.payment_status === "initiated" ? "Complete Payment" : "Pay Now"}
                      </Button>
                    )}
                    {/* Rating for completed bookings */}
                    {booking.status === "completed" && (
                      <div className="mt-2 pt-2 border-t border-white/5">
                        {hasRated("driver", booking.booking_id) ? (
                          <div className="flex items-center gap-2">
                            <div className="flex">
                              {[1,2,3,4,5].map(s => (
                                <Star key={s} className={`w-3.5 h-3.5 ${s <= getRating("driver", booking.booking_id)?.rating ? 'text-[#D4AF37] fill-[#D4AF37]' : 'text-white/20'}`} />
                              ))}
                            </div>
                            <span className="text-xs text-[#A1A1AA]">Rated</span>
                          </div>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            className="border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37]/10 text-xs"
                            onClick={() => openRatingDialog("driver", booking.booking_id)}
                            data-testid={`rate-driver-${booking.booking_id}`}
                          >
                            <Star className="w-3 h-3 mr-1" />
                            Rate Service
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Payment Package Selection Dialog */}
        <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
          <DialogContent className="bg-[#0D0E14] border-white/10 text-white max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-[#D4AF37]" />
                Select Driver Package
              </DialogTitle>
              <p className="text-sm text-[#A1A1AA]">Choose a service package for your booking</p>
            </DialogHeader>
            <div className="space-y-3 mt-2">
              {packages.map((pkg) => (
                <div
                  key={pkg.package_id}
                  className={`rounded-xl p-4 cursor-pointer transition-all border ${
                    selectedPackage === pkg.package_id
                      ? "bg-[#D4AF37]/10 border-[#D4AF37]/50 ring-1 ring-[#D4AF37]/30"
                      : "bg-white/5 border-white/10 hover:border-white/20"
                  }`}
                  onClick={() => setSelectedPackage(pkg.package_id)}
                  data-testid={`package-${pkg.package_id}`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                        selectedPackage === pkg.package_id ? "border-[#D4AF37]" : "border-white/30"
                      }`}>
                        {selectedPackage === pkg.package_id && (
                          <div className="w-2 h-2 rounded-full bg-[#D4AF37]" />
                        )}
                      </div>
                      <h4 className="font-medium text-white text-sm sm:text-base">{pkg.name}</h4>
                    </div>
                    <span className="text-lg font-semibold text-[#D4AF37]">₹{pkg.amount}</span>
                  </div>
                  <p className="text-xs sm:text-sm text-[#A1A1AA] ml-6">{pkg.description}</p>
                </div>
              ))}

              <Button
                className="w-full py-6 btn-primary rounded-xl text-base font-medium mt-4"
                disabled={paymentLoading || !selectedPackage}
                onClick={() => paymentBookingId && handlePayment(paymentBookingId)}
                data-testid="proceed-to-payment-btn"
              >
                {paymentLoading ? (
                  <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Redirecting to payment...</>
                ) : (
                  <><CreditCard className="w-4 h-4 mr-2" /> Pay ₹{packages.find(p => p.package_id === selectedPackage)?.amount || 0}</>
                )}
              </Button>

              <p className="text-xs text-center text-[#A1A1AA]">
                Secure payment powered by Stripe. You can cancel before completing.
              </p>
            </div>
          </DialogContent>
        </Dialog>

        {/* Rating Dialog */}
        <Dialog open={ratingDialogOpen} onOpenChange={setRatingDialogOpen}>
          <DialogContent className="bg-[#0D0E14] border-white/10 text-white max-w-md">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl flex items-center gap-2">
                <Star className="w-5 h-5 text-[#D4AF37]" />
                Rate Your Experience
              </DialogTitle>
              <p className="text-sm text-[#A1A1AA]">
                How was your {ratingServiceType === "valet" ? "valet" : "driver"} service?
              </p>
            </DialogHeader>
            <div className="space-y-5 mt-2">
              {/* Star Rating */}
              <div className="flex justify-center gap-2" data-testid="star-rating-picker">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    className="p-1 transition-transform hover:scale-125"
                    onMouseEnter={() => setRatingHover(star)}
                    onMouseLeave={() => setRatingHover(0)}
                    onClick={() => setRatingStars(star)}
                    data-testid={`star-${star}`}
                  >
                    <Star
                      className={`w-8 h-8 sm:w-10 sm:h-10 transition-colors ${
                        star <= (ratingHover || ratingStars)
                          ? "text-[#D4AF37] fill-[#D4AF37]"
                          : "text-white/20"
                      }`}
                    />
                  </button>
                ))}
              </div>
              <p className="text-center text-sm text-[#A1A1AA]">
                {ratingStars === 1 && "Poor"}
                {ratingStars === 2 && "Below Average"}
                {ratingStars === 3 && "Good"}
                {ratingStars === 4 && "Very Good"}
                {ratingStars === 5 && "Excellent!"}
              </p>

              {/* Review */}
              <div>
                <Textarea
                  placeholder="Write a review (optional)..."
                  value={ratingReview}
                  onChange={(e) => setRatingReview(e.target.value)}
                  className="bg-white/5 border-white/10 text-white placeholder:text-[#A1A1AA] min-h-[80px] rounded-xl resize-none"
                  data-testid="rating-review-input"
                />
              </div>

              {/* Submit */}
              <Button
                className="w-full py-5 btn-primary rounded-xl text-base font-medium"
                disabled={ratingLoading || ratingStars === 0}
                onClick={handleSubmitRating}
                data-testid="submit-rating-btn"
              >
                {ratingLoading ? (
                  <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Submitting...</>
                ) : (
                  <>Submit Rating</>
                )}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default CustomerDashboard;
